/**
 * useKeyboardShortcuts - Keyboard shortcuts for zoom control
 *
 * Provides keyboard shortcuts for common zoom operations in critique mode:
 * - '0' key: Fit zoom (fill viewport)
 * - '1' key: 100% zoom (true pixel-perfect)
 * - '+' or '=' key: Increment to next zoom level
 * - '-' or '_' key: Decrement to previous zoom level
 *
 * Features:
 * - Disabled when typing in text inputs/textareas
 * - Respects zoom level bounds (Fit minimum, 400% maximum)
 * - Uses ZOOM_LEVELS array for predictable increments
 *
 * @module useKeyboardShortcuts
 */

import { useEffect } from 'react';
import { ZOOM_LEVELS, type ZoomLevel } from './useZoomPan';

export interface UseKeyboardShortcutsProps {
  /** Current zoom level */
  currentZoom: ZoomLevel;

  /** Callback when zoom should change via keyboard */
  onZoomChange: (level: ZoomLevel) => void;

  /** Whether shortcuts are enabled (default: true) */
  enabled?: boolean;
}

/**
 * Check if the current focus is in a text input element
 * Prevents shortcuts from firing when user is typing
 */
function isTypingInInput(): boolean {
  const activeElement = document.activeElement;
  if (!activeElement) return false;

  const tagName = activeElement.tagName.toLowerCase();
  const isContentEditable = activeElement.getAttribute('contenteditable') === 'true';

  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    isContentEditable
  );
}

/**
 * Get next zoom level in sequence
 * Returns 400 (max) if already at maximum
 */
function getNextZoomLevel(current: ZoomLevel): ZoomLevel {
  const currentIndex = ZOOM_LEVELS.indexOf(current);
  if (currentIndex === -1 || currentIndex === ZOOM_LEVELS.length - 1) {
    return 400; // Maximum zoom
  }
  return ZOOM_LEVELS[currentIndex + 1];
}

/**
 * Get previous zoom level in sequence
 * Returns 'fit' (min) if already at minimum
 */
function getPreviousZoomLevel(current: ZoomLevel): ZoomLevel {
  const currentIndex = ZOOM_LEVELS.indexOf(current);
  if (currentIndex === -1 || currentIndex === 0) {
    return 'fit'; // Minimum zoom
  }
  return ZOOM_LEVELS[currentIndex - 1];
}

/**
 * Hook to enable keyboard shortcuts for zoom control
 *
 * @example
 * ```typescript
 * useKeyboardShortcuts({
 *   currentZoom: sessionZoomLevel,
 *   onZoomChange: setSessionZoomLevel,
 *   enabled: true
 * });
 * ```
 */
export function useKeyboardShortcuts({
  currentZoom,
  onZoomChange,
  enabled = true,
}: UseKeyboardShortcutsProps): void {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // T042: Ignore shortcuts when typing in text fields
      if (isTypingInInput()) return;

      // Ignore if modifier keys pressed (Cmd, Ctrl, Alt)
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      switch (event.key) {
        case '0':
          // T038: '0' key → Fit zoom
          event.preventDefault();
          onZoomChange('fit');
          break;

        case '1':
          // T039: '1' key → 100% zoom
          event.preventDefault();
          onZoomChange(100);
          break;

        case '+':
        case '=':
          // T040: '+' or '=' key → increment zoom
          // T043: Cap at 400% maximum
          event.preventDefault();
          onZoomChange(getNextZoomLevel(currentZoom));
          break;

        case '-':
        case '_':
          // T041: '-' or '_' key → decrement zoom
          // T043: Cap at Fit minimum
          event.preventDefault();
          onZoomChange(getPreviousZoomLevel(currentZoom));
          break;

        default:
          // No action for other keys
          break;
      }
    };

    // T037: Document-level keydown listener
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentZoom, onZoomChange, enabled]);
}
